import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, MailCheck, KeyRound, ShieldCheck, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";

const INPUT = "w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2.5 text-sm";
const BTN = "w-full bg-[#c8aa6e] text-black hover:bg-[#d4b982] active:scale-[0.98] px-4 py-2.5 font-medium text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60";

const detail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fallback;
};

export function AuthShell({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] p-8">
      <div className="w-full max-w-sm fade-up" data-testid={testid}>
        <img src="/radarx-logo.png" alt="RadarX" className="h-10 w-auto mb-8" />
        <div className="flex items-center gap-3 mb-2">
          {Icon && <Icon className="w-5 h-5 text-[#c8aa6e]" strokeWidth={1.5} />}
          <h2 className="font-head text-2xl font-normal text-[#fafafa]">{title}</h2>
        </div>
        {subtitle && <p className="text-sm text-[#71717a] mb-8">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

export function DevLink({ link }) {
  if (!link) return null;
  return (
    <div data-testid="dev-link" className="mt-4 border border-[#c8aa6e]/40 bg-[#c8aa6e]/5 px-3 py-2 text-xs text-[#a1a1aa]">
      <div className="label-tiny mb-1 text-[#c8aa6e]">Modo simulação de e-mail</div>
      O envio de e-mails ainda não está configurado. Use este link:
      <a href={link} className="block mt-1 text-[#c8aa6e] break-all hover:underline" data-testid="dev-link-anchor">{link}</a>
    </div>
  );
}

const Notice = ({ ok, children, testid }) => (
  <div data-testid={testid} className={`text-sm px-3 py-3 border ${ok ? "border-[#10b981]/40 bg-[#10b981]/5 text-[#d1fae5]" : "border-[#ef4444]/30 bg-[#ef4444]/5 text-[#fca5a5]"} flex gap-2`}>
    {ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
    <span>{children}</span>
  </div>
);

function PasswordField({ value, onChange, testid, placeholder = "Mínimo 6 caracteres" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testid} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
        className={INPUT + " pr-10"} placeholder={placeholder} required minLength={6} />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-0 flex items-center px-3 text-[#71717a] hover:text-[#c8aa6e]">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ busy: true, ok: false, msg: "" });
  useEffect(() => {
    if (!token) { setState({ busy: false, ok: false, msg: "Link de confirmação inválido." }); return; }
    api.post("/auth/verify-email", { token })
      .then(({ data }) => setState({ busy: false, ok: true, msg: data.message }))
      .catch((err) => setState({ busy: false, ok: false, msg: detail(err, "Link de confirmação inválido ou expirado.") }));
  }, [token]);
  return (
    <AuthShell icon={MailCheck} title="Confirmação de e-mail" testid="verify-email-page">
      {state.busy ? (
        <div className="text-sm text-[#71717a] flex items-center gap-2"><Loader2 className="w-4 h-4 spin" /> A confirmar…</div>
      ) : (
        <Notice ok={state.ok} testid="verify-result">{state.msg}</Notice>
      )}
      <a href="/login" data-testid="verify-go-login" className={BTN + " mt-6"}>Ir para o início de sessão</a>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setRes({ ok: data.reason !== "email_unverified", msg: data.message, link: data.dev_link });
    } catch (err) {
      setRes({ ok: false, msg: detail(err, "Não foi possível processar o pedido.") });
    } finally { setBusy(false); }
  };
  return (
    <AuthShell icon={KeyRound} title="Recuperar palavra-passe" subtitle="Indique o e-mail da sua conta. Enviaremos um link para repor a palavra-passe." testid="forgot-page">
      <form onSubmit={submit}>
        <label className="label-tiny block mb-2">Email</label>
        <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT + " mb-5"} placeholder="o.teu@email.com" required />
        {res && <div className="mb-4"><Notice ok={res.ok} testid="forgot-result">{res.msg}</Notice><DevLink link={res.link} /></div>}
        <button data-testid="forgot-submit" type="submit" disabled={busy} className={BTN}>
          {busy && <Loader2 className="w-4 h-4 spin" />} Enviar link
        </button>
      </form>
      <p className="text-sm text-[#71717a] mt-6 text-center">
        <a href="/login" className="text-[#c8aa6e] hover:text-[#d4b982]" data-testid="forgot-back">Voltar ao início de sessão</a>
      </p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setRes({ ok: false, msg: "As palavras-passe não coincidem." }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      setRes({ ok: true, msg: data.message, done: true });
    } catch (err) {
      setRes({ ok: false, msg: detail(err, "Link de reposição inválido ou expirado.") });
    } finally { setBusy(false); }
  };
  return (
    <AuthShell icon={KeyRound} title="Repor palavra-passe" subtitle="Escolha uma nova palavra-passe para a sua conta." testid="reset-page">
      {res?.done ? (
        <>
          <Notice ok testid="reset-result">{res.msg}</Notice>
          <a href="/login" data-testid="reset-go-login" className={BTN + " mt-6"}>Iniciar sessão</a>
        </>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div><label className="label-tiny block mb-2">Nova palavra-passe</label><PasswordField value={pw} onChange={setPw} testid="reset-password" /></div>
          <div><label className="label-tiny block mb-2">Confirmar palavra-passe</label><PasswordField value={pw2} onChange={setPw2} testid="reset-password2" placeholder="Repita a palavra-passe" /></div>
          {res && <Notice ok={res.ok} testid="reset-result">{res.msg}</Notice>}
          <button data-testid="reset-submit" type="submit" disabled={busy || !token} className={BTN}>
            {busy && <Loader2 className="w-4 h-4 spin" />} Guardar nova palavra-passe
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    if (!token) { setRes({ ok: false, msg: "Convite inválido." }); return; }
    api.get("/auth/invite-info", { params: { token } })
      .then(({ data }) => { setInfo(data); setName(data.name || ""); })
      .catch((err) => setRes({ ok: false, msg: detail(err, "Convite inválido ou expirado."), fatal: true }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setRes({ ok: false, msg: "As palavras-passe não coincidem." }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password: pw, name: name.trim() || null });
      if (data.logged_in && data.access_token) {
        localStorage.setItem("token", data.access_token);
        await refresh();
        navigate("/");
        return;
      }
      setRes({ ok: true, msg: data.message, done: true });
    } catch (err) {
      setRes({ ok: false, msg: detail(err, "Convite inválido ou expirado.") });
    } finally { setBusy(false); }
  };

  return (
    <AuthShell icon={ShieldCheck} title="Bem-vindo ao RadarX" subtitle={info ? `A sua conta (${info.email}) foi criada pelo administrador. Defina a sua palavra-passe para começar.` : "A validar convite…"} testid="set-password-page">
      {res?.fatal || res?.done ? (
        <>
          <Notice ok={!!res.ok} testid="set-password-result">{res.msg}</Notice>
          <a href="/login" data-testid="set-password-go-login" className={BTN + " mt-6"}>Ir para o início de sessão</a>
        </>
      ) : info && (
        <form onSubmit={submit} className="space-y-5">
          <div><label className="label-tiny block mb-2">Nome</label>
            <input data-testid="set-password-name" value={name} onChange={(e) => setName(e.target.value)} className={INPUT} placeholder="O seu nome" /></div>
          <div><label className="label-tiny block mb-2">Palavra-passe</label><PasswordField value={pw} onChange={setPw} testid="set-password-password" /></div>
          <div><label className="label-tiny block mb-2">Confirmar palavra-passe</label><PasswordField value={pw2} onChange={setPw2} testid="set-password-password2" placeholder="Repita a palavra-passe" /></div>
          {res && <Notice ok={res.ok} testid="set-password-result">{res.msg}</Notice>}
          <button data-testid="set-password-submit" type="submit" disabled={busy} className={BTN}>
            {busy && <Loader2 className="w-4 h-4 spin" />} Definir palavra-passe e entrar
          </button>
        </form>
      )}
    </AuthShell>
  );
}
